/**
 * Keeps an admin who sees only their own leads out of an investor page opened
 * by link.
 *
 * The lists that lead to an investor's MF dashboard and planner are filtered to
 * the admin's leads (see `isVisibleLead`), but a uuid in a URL skips the list.
 * So the page is mounted only once the investor is confirmed to be the admin's
 * lead — nothing of the investor's is loaded before that.
 *
 * Superusers and admins with `can_see_all` pass straight through with no call.
 * Everyone else is checked against `/user/user_list/` — the directory, and the
 * `leadowner` field, the Users page filters on. If the check cannot complete,
 * the page stays closed rather than risk showing someone else's investor.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { makeGETRequestOnUserService } from "../../Api/api";
import { canSeeAllLeads, isVisibleLead } from "../../Helpers/adminPermissions";
import type { IUser } from "../YieldCalculator/Interfaces";
import styles from "./investor-lead-gate.module.css";

type LeadAccess = "checking" | "allowed" | "denied" | "error";

interface InvestorLeadGateProps {
  /** Absent on pages that are not about one investor; those always pass. */
  userUuid?: string;
  /** Where a refused admin is sent — a list they are allowed to see. */
  backTo: { label: string; path: string };
  children: React.ReactNode;
}

const InvestorLeadGate = ({ userUuid, backTo, children }: InvestorLeadGateProps) => {
  const history = useHistory();
  const [access, setAccess] = useState<LeadAccess>(() =>
    !userUuid || canSeeAllLeads() ? "allowed" : "checking"
  );
  // Only the latest check may settle the state, and none after unmount.
  const requestRef = useRef(0);

  const check = useCallback(async () => {
    const request = ++requestRef.current;
    if (!userUuid || canSeeAllLeads()) {
      setAccess("allowed");
      return;
    }
    setAccess("checking");
    try {
      const response: any = await makeGETRequestOnUserService("/user/user_list/");
      const users: IUser[] = Array.isArray(response?.data) ? response.data : [];
      const investor = users.find((user) => user.uuid === userUuid);
      if (request !== requestRef.current) return;
      setAccess(investor && isVisibleLead(investor.leadowner) ? "allowed" : "denied");
    } catch (error) {
      if (request === requestRef.current) setAccess("error");
    }
  }, [userUuid]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  if (access === "allowed") return <>{children}</>;

  const back = (
    <button type="button" className={styles.primary} onClick={() => history.push(backTo.path)}>
      {backTo.label}
    </button>
  );

  return (
    <main className={styles.page}>
      {access === "checking" ? (
        <div className={styles.card} aria-busy="true">
          <div className={styles.spinner} />
          <p className={styles.checking}>Checking this investor is one of your leads…</p>
        </div>
      ) : access === "denied" ? (
        <div className={styles.card} role="alert">
          <span className={styles.icon}>
            <LockOutlinedIcon />
          </span>
          <h1>This investor isn&apos;t one of your leads</h1>
          <p>
            Your admin account can open only the investors whose lead owner is
            you. If this investor needs attention, ask a superuser or an admin
            who can see all users.
          </p>
          <div className={styles.actions}>{back}</div>
        </div>
      ) : (
        <div className={styles.card} role="alert">
          <span className={`${styles.icon} ${styles.iconWarn}`}>
            <ErrorOutlineIcon />
          </span>
          <h1>We couldn&apos;t confirm this investor is one of your leads</h1>
          <p>
            The user directory did not load, so the page stays closed rather
            than risk showing someone else&apos;s investor.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={check}>
              Try again
            </button>
            {back}
          </div>
        </div>
      )}
    </main>
  );
};

export default InvestorLeadGate;
